import React from "react";
import { useTheme } from "@mui/material/styles";
import {
  MenuItem,
  SelectProps as MuiSelectFieldProps,
  SelectChangeEvent,
  SxProps,
} from "@mui/material";

import { Variants, SizeStyles, Types, CustomStyles } from "./SelectField.variants";
import {
  CustomInputLabel,
  CustomSelectField,
  SelectFieldContainer,
  InfoMessageContainer,
  InfoTypography,
  CustomMenuItem,
  ErrorMessageTypography,
  IconBox
} from "./SelectField.styles";
import { Icon } from "../../icons";
import { InfoMessage } from "..";

export type OptionType = string | number;
export interface CustomVariantSelectFieldProps
  extends Pick<
    MuiSelectFieldProps,
    | "multiple"
    | "fullWidth"
    | "autoWidth"
    | "onClick"
    | "onMouseDown"
    | "MenuProps"
    | "inputProps"
    | "onOpen"
    | "onClose"
    | "open"
    | "id"
    | "value"
    | "ref"
    | "sx"
  > {
  variant?: "primary" | "secondary";
  type?: "default" | "numbersOnly";
  size?: "xs" | "sm" | "md" | "lg";
  onChange: (event: SelectChangeEvent<unknown>) => void;
  onBlurCapture?: (event: React.SyntheticEvent<Element, Event>) => void;
  options: Record<OptionType, boolean>;
  label?: string;
  infoMessage?: string;
  placeholder?: string;
  error?: boolean;
  errorMessage?: string;
  errorBackground?: boolean;
  disabled?: boolean;
  leftIcon?: React.ReactElement | null;
  disabledIcon?: React.ReactElement | null;
  required?: boolean;
  customInputLabelSx?: SxProps
}

export const SelectField = ({
  variant = "primary",
  type = "default",
  size = "md",
  options,
  infoMessage,
  label,
  placeholder,
  error = false,
  errorMessage = "",
  errorBackground = false,
  disabled = false,
  disabledIcon = null,
  leftIcon = null,
  onChange,
  onBlurCapture,
  MenuProps,
  fullWidth,
  required,
  customInputLabelSx,
  sx,
  ...rest
}: CustomVariantSelectFieldProps) => {
  const theme = useTheme();

  const variantStylesFn = Variants(theme);
  const typeStylesFn = Types(theme);
  const sizeStylesFn = SizeStyles(theme);
  const customStylesFn = CustomStyles(theme);
  const conditionalFullWidth = fullWidth ? customStylesFn['fullWidth'] : {};

  return (
    <SelectFieldContainer sx={{ ...conditionalFullWidth }}>
      {label && (
        <CustomInputLabel sx={customInputLabelSx} shrink htmlFor="custom-SelectField-input">
          {label}
        </CustomInputLabel>
      )}
      <CustomSelectField
        id="custom-SelectField-input"
        data-cy="select-list"
        displayEmpty
        disabled={disabled}
        onChange={(event) => onChange(event)}
        sx={{
          ...variantStylesFn[variant],
          ...typeStylesFn[type],
          ...conditionalFullWidth,
          ...sizeStylesFn[size],
          ...sx
        }}
        onClose={onBlurCapture}
        MenuProps={{
          ...MenuProps,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          PaperProps: {
            style: {
              maxHeight: 330,
              width: 226,
              boxShadow: theme.boxShadow["shadow.1"],
              borderRadius: "1rem",
              backgroundColor: theme.colors["neutral.900"],
              padding: `${theme.space._400} 0`,
              marginTop: theme.space._300,
            },
          },
        }}
        renderValue={(selected: any) => {
          if (Array.isArray(selected) && selected.length > 1) {
            return `${selected.length} Selected`;
          } else if (Array.isArray(selected) && !selected.length) {
            return placeholder
          } else {
            return selected || placeholder;
          }
        }}
        IconComponent={(props) => (
          disabled && disabledIcon ? <IconBox {...props}>{disabledIcon}</IconBox> : <Icon icon="ChevronDown" size="sm" {...props} />
        )}
        {...rest}
        className={error ? "is-error" : ""}
      >
        {infoMessage && (
          <InfoMessageContainer>
            <Icon icon="InformationCircle" />
            <InfoTypography>{infoMessage}</InfoTypography>
          </InfoMessageContainer>
        )}
        {options && Object.keys(options).map((key) => (
          <CustomMenuItem key={key} value={key} data-cy={`select-list-item-${key}`} disabled={options[key]}>
            {key}
          </CustomMenuItem>
        ))}
      </CustomSelectField>
      {error && errorMessage != "" && (
        <InfoMessage hasBackground={errorBackground} hasIcon={false} variant="error" message={errorMessage} />
      )}
    </SelectFieldContainer>
  );
};

export interface SelectFieldProps
  extends React.ComponentProps<typeof SelectField> {
  as?: React.ElementType;
}

SelectField.displayName = "SelectField";
